import { useMemo } from 'react';
import { callBackend } from '../utils/api';
import { createOptimizerOperationId } from '../utils/optimizerConstraintEditor';
import { expectedRunProvider } from '../utils/optimizerObjectives';
import { isDefiniteOptimizerMutationRejection, optimizerErrorCode, optimizerErrorMessage,
    optimizerCommittedRefreshError, requireOptimizerPortfolioList } from '../components/optimizer/optimizerPortfolioMutationContract';
import { OPTIMIZER_SAVED_RECEIPT_CONTRACTS, OPTIMIZER_V6_RECEIPT_CONTRACT,
    OPTIMIZER_V7_RECEIPT_CONTRACT, OPTIMIZER_V8_RECEIPT_CONTRACT, OPTIMIZER_V9_RECEIPT_CONTRACT,
    fetchOptimizerV2PortfolioDetail, requireOptimizerV2RunResponse,
    requireOptimizerV2SaveResponse } from '../utils/optimizerGenericGroupContract';
import { OPTIMIZER_RESTORATION_REQUIRED_MESSAGE, buildOptimizerRunCommand,
    buildOptimizerSaveCommand, optimizerRunPreflightError,
    savedOptimizerRunConfiguration } from '../utils/optimizerRunCommand';

// Receipt eligibility and user-scoped list/barriers remain with their models.
// This hook coordinates only save/reoptimization phases against those owners.
export default function useOptimizerPortfolioPersistence({
    operations, catalogue, results, workspace, feedback, execution,
}) {
    return useMemo(() => {
        const buildRefreshFailureMessage = ({
            portfolioName,
            baseMessage,
        }) => {
            const prefix = portfolioName
                ? `Re-optimization failed for "${portfolioName}".`
                : 'Re-optimization failed.';
            const detail = baseMessage ? ` ${baseMessage}` : '';
            const existingVersionNote = ' The existing saved portfolio was left unchanged.';

            return `${prefix}${detail}${existingVersionNote} You can keep the existing saved version, or load it in the Optimizer tab and rerun it manually.`;
        };

        const classifyAndRecordMutationFailure = ({
            phase,
            error,
            userScope,
            barrierHandle,
            operation,
        }) => {
            let status = 'ERROR';
            if (phase === 'committed' || (phase === 'dispatched' && error?.optimizerMutationCommitted)) {
                catalogue.setBarrierOutcome(
                    userScope,
                    barrierHandle,
                    'committed',
                    error?.optimizerMutationIncomplete !== true,
                );
                status = 'COMMITTED_REFRESH_REQUIRED';
            } else if (phase === 'dispatched' && isDefiniteOptimizerMutationRejection(error)) {
                catalogue.clearBarrier(userScope, barrierHandle);
                status = 'REJECTED';
            } else if (phase === 'dispatched') {
                catalogue.setBarrierOutcome(userScope, barrierHandle, 'unknown');
                status = 'OUTCOME_UNKNOWN';
            }
            return {
                status,
                current: operations.isCurrent(operation),
            };
        };

        const reconcileCommittedMutation = async ({
            operation,
            barrierHandle,
            findPortfolio,
            missingMessage,
        }) => {
            try {
                const response = await catalogue.refresh({
                    force: true,
                    isCurrentRequest: () => operations.isCurrent(operation),
                    mutation: { barrierHandle },
                });
                if (response?.status === 'superseded') {
                    throw new Error('Optimizer portfolio list refresh was superseded.');
                }
                const portfolios = requireOptimizerPortfolioList(response);
                if (response?.optimizerMutationReconciled !== true) {
                    throw new Error('Optimizer portfolio list did not reconcile the completed mutation.');
                }
                const portfolio = portfolios.find(findPortfolio);
                if (!portfolio) throw new Error(missingMessage);
                return portfolio;
            } catch (error) {
                throw optimizerCommittedRefreshError(error);
            }
        };

        const saveOptimizationAndWhatIfPortfolioData = async (finalInputValue, isManual) => {
            const normalizedName = typeof finalInputValue === 'string'
                ? finalInputValue.trim()
                : finalInputValue;
            if (isManual && (!normalizedName || normalizedName.length === 0)) {
                feedback.error('Please enter a portfolio name before saving.');
                return { success: false, status: 'ERROR' };
            }

            const completedRun = results.captureSavableRun();
            if (!completedRun) {
                feedback.error('A complete receipt-backed optimizer run is required before saving.');
                return { success: false, status: 'ERROR' };
            }

            const operation = operations.begin();
            const { userScope } = operation;
            let mutationPhase = 'not-started';
            let mutationBarrierHandle = null;
            feedback.begin();
            try {
                operations.assertCurrent(operation);

                let targetPortfolio = null;

                if (!isManual) {
                    targetPortfolio = catalogue.portfolios?.find(
                        portfolio => portfolio?.value === normalizedName
                    );
                    if (!targetPortfolio?.id) {
                        throw new Error('The saved optimizer portfolio to update was not found.');
                    }
                    if (targetPortfolio?.optimizerMutationBlocked) {
                        throw new Error('This optimizer portfolio has an unresolved mutation. Reload the saved portfolio list before making another change.');
                    }
                    if (
                        targetPortfolio?.receiptStatus !== 'available'
                        || !Number.isInteger(targetPortfolio?.portfolioVersion)
                    ) {
                        throw new Error('This saved optimizer portfolio is unavailable.');
                    }
                }

                if (![
                    OPTIMIZER_V6_RECEIPT_CONTRACT,
                    OPTIMIZER_V7_RECEIPT_CONTRACT,
                    OPTIMIZER_V8_RECEIPT_CONTRACT,
                    OPTIMIZER_V9_RECEIPT_CONTRACT,
                ].includes(completedRun.receiptContract)) {
                    throw new Error(
                        'The optimizer result contract is unsupported. Run a new optimization before saving.',
                    );
                }
                const request = buildOptimizerSaveCommand({
                    targetPortfolio: isManual ? null : targetPortfolio,
                    portfolioName: normalizedName,
                    receiptId: completedRun.receiptId,
                    saveOperationId: createOptimizerOperationId(),
                });

                mutationBarrierHandle = catalogue.recordBarrier(userScope, {
                    portfolioId: targetPortfolio?.id ?? null,
                    portfolioName: normalizedName,
                    expected: 'present',
                });
                if (!mutationBarrierHandle) {
                    throw new Error('Optimizer save mutation barrier could not be established.');
                }
                mutationPhase = 'dispatched';
                const saveResponse = await callBackend(
                    'save_optimizer_portfolio_v2',
                    'POST',
                    request
                );
                const saveAcknowledgement = requireOptimizerV2SaveResponse(saveResponse, request);
                mutationPhase = 'committed';
                results.revokeSaveEligibility();
                if (!catalogue.setBarrierOutcome(
                    userScope,
                    mutationBarrierHandle,
                    'committed',
                    true,
                    {
                        portfolioId: saveAcknowledgement.portfolio_id,
                        portfolioVersion: saveAcknowledgement.committed_portfolio_version,
                        receiptId: saveAcknowledgement.receipt_id,
                    },
                )) {
                    return { success: false, status: 'SUPERSEDED' };
                }
                if (!operations.isCurrent(operation)) {
                    return { success: false, status: 'SUPERSEDED' };
                }

                const savedPortfolio = await reconcileCommittedMutation({
                    operation,
                    barrierHandle: mutationBarrierHandle,
                    findPortfolio: (portfolio) => (
                        String(portfolio?.id) === String(saveAcknowledgement.portfolio_id)
                        && (portfolio?.portfolioVersion ?? portfolio?.portfolio_version)
                            === saveAcknowledgement.committed_portfolio_version
                        && (portfolio?.receiptId ?? portfolio?.receipt_id)
                            === saveAcknowledgement.receipt_id
                    ),
                    missingMessage: 'Saved optimizer portfolio was absent from the refreshed list.',
                });
                results.selectSavedPortfolio(normalizedName);
                feedback.error(null);
                return {
                    success: true,
                    status: 'SUCCESS',
                    portfolio: savedPortfolio,
                };
            } catch (err) {
                console.error('Error running saveOptimizationAndWhatIfPortfolioData:', err);
                const failure = classifyAndRecordMutationFailure({
                    phase: mutationPhase,
                    error: err,
                    userScope,
                    barrierHandle: mutationBarrierHandle,
                    operation,
                });
                if (!failure.current) {
                    return {
                        success: false,
                        status: 'SUPERSEDED',
                    };
                }
                const detail = optimizerErrorMessage(err, 'Unknown error.');
                const code = optimizerErrorCode(err);
                if (failure.status === 'OUTCOME_UNKNOWN') {
                    results.revokeSaveEligibility();
                }
                const failureMessage = failure.status === 'COMMITTED_REFRESH_REQUIRED'
                    ? `The portfolio save was acknowledged, but its complete saved state could not be confirmed. Do not retry the save; reload the application and inspect the saved portfolio. ${detail}`
                    : failure.status === 'REJECTED'
                        ? code === 'optimizer_portfolio_name_conflict'
                            ? 'That portfolio name is already in use. Choose a different name and save again.'
                            : code === 'optimizer_portfolio_limit_reached'
                                ? detail
                                : `The portfolio was not saved. ${detail}`
                        : failure.status === 'OUTCOME_UNKNOWN'
                            ? `The save outcome could not be confirmed. Do not retry. Reload the application and inspect the saved portfolio before running and saving again. ${detail}`
                            : `Failed to save. ${detail}`;
                feedback.error(failureMessage);
                return {
                    success: false,
                    status: failure.status,
                    error: err,
                };
            } finally {
                if (operations.isCurrent(operation)) {
                    feedback.finish();
                }
            }
        };

        const refreshSavedOptimizedPortfolio = async (portfolioLike) => {
            const operation = operations.begin();
            const { userScope } = operation;
            let targetPortfolio = null;
            let mutationPhase = 'not-started';
            let mutationBarrierHandle = null;
            feedback.begin();

            try {
                operations.assertCurrent(operation);
                targetPortfolio = typeof portfolioLike === 'object'
                    ? portfolioLike
                    : catalogue.portfolios?.find((portfolio) => (
                        String(portfolio?.id) === String(portfolioLike) ||
                        portfolio?.value === portfolioLike
                    ));

                if (!targetPortfolio?.id) {
                    throw new Error('Saved optimized portfolio not found.');
                }
                if (targetPortfolio?.type !== 'opt') {
                    throw new Error('Only saved optimized portfolios can be re-optimized.');
                }
                if (targetPortfolio?.optimizerMutationBlocked) {
                    throw new Error('This optimizer portfolio has an unresolved mutation. Reload the saved portfolio list before making another change.');
                }

                if (Number(targetPortfolio?.compatibility?.missing_security_count) > 0) {
                    return {
                        success: false,
                        status: 'WATCHLIST_RESTORATION_REQUIRED',
                        message: OPTIMIZER_RESTORATION_REQUIRED_MESSAGE,
                        portfolio: targetPortfolio,
                    };
                }

                if (
                    targetPortfolio?.receiptStatus !== 'available'
                    || !Number.isInteger(targetPortfolio?.portfolioVersion)
                ) {
                    throw new Error('This saved optimizer portfolio is unavailable.');
                }
                if (!OPTIMIZER_SAVED_RECEIPT_CONTRACTS.includes(
                    targetPortfolio.receiptContractVersion,
                )) {
                    throw new Error(
                        'The saved optimizer receipt contract is unsupported.',
                    );
                }
                const detail = await fetchOptimizerV2PortfolioDetail(
                    targetPortfolio.id,
                    targetPortfolio.portfolioVersion,
                );
                operations.assertCurrent(operation);
                if (detail.receipt_status !== 'available') {
                    throw new Error('This optimizer portfolio has no receipt-backed detail.');
                }
                if (Number(detail?.compatibility?.missing_security_count) > 0) {
                    return {
                        success: false,
                        status: 'WATCHLIST_RESTORATION_REQUIRED',
                        message: OPTIMIZER_RESTORATION_REQUIRED_MESSAGE,
                        portfolio: {
                            ...targetPortfolio,
                            compatibility: detail.compatibility,
                        },
                    };
                }
                // Exactly what loading this portfolio in the Optimizer tab and
                // clicking Run would send: the same saved configuration, preflight
                // and command builder, against the same current contract.
                const { capability, catalog } = await workspace.resolveContract();
                operations.assertCurrent(operation);
                const configuration = savedOptimizerRunConfiguration(detail, targetPortfolio, {
                    catalog,
                });
                const preflightError = optimizerRunPreflightError({
                    ...configuration,
                    capability,
                    posSizingOptimizerContext: workspace.posSizingOptimizerContext,
                });
                if (preflightError) {
                    throw new Error(preflightError);
                }
                const runCommand = buildOptimizerRunCommand({
                    ...configuration,
                    posSizingContextDocumentSha256:
                        workspace.posSizingOptimizerContext?.context_document_sha256 ?? null,
                    runOperationId: createOptimizerOperationId(),
                });
                const rawRunResponse = await execution.runCommand(runCommand, {
                    isCurrent: () => operations.isCurrent(operation),
                });
                const expectedRunResponse = {
                    runOperationId: runCommand.run_operation_id,
                    objective: runCommand.objective,
                    provider: expectedRunProvider(runCommand, capability.provider),
                };
                const runResponse = requireOptimizerV2RunResponse(
                    rawRunResponse,
                    expectedRunResponse,
                );
                operations.assertCurrent(operation);
                mutationBarrierHandle = catalogue.recordBarrier(userScope, {
                    portfolioId: targetPortfolio.id,
                    portfolioName: targetPortfolio.value,
                    expected: 'present',
                });
                if (!mutationBarrierHandle) {
                    throw new Error('Optimizer refresh mutation barrier could not be established.');
                }

                mutationPhase = 'dispatched';
                const saveCommand = buildOptimizerSaveCommand({
                    targetPortfolio,
                    receiptId: runResponse.receipt.receipt_id,
                    saveOperationId: createOptimizerOperationId(),
                });
                const saveResponse = await callBackend(
                    'save_optimizer_portfolio_v2',
                    'POST',
                    saveCommand,
                );
                const saveAcknowledgement = requireOptimizerV2SaveResponse(
                    saveResponse,
                    saveCommand,
                );
                mutationPhase = 'committed';
                if (!catalogue.setBarrierOutcome(
                    userScope,
                    mutationBarrierHandle,
                    'committed',
                    true,
                    {
                        portfolioId: saveAcknowledgement.portfolio_id,
                        portfolioVersion: saveAcknowledgement.committed_portfolio_version,
                        receiptId: saveAcknowledgement.receipt_id,
                    },
                )) {
                    return { success: false, status: 'SUPERSEDED' };
                }
                if (!operations.isCurrent(operation)) {
                    return { success: false, status: 'SUPERSEDED' };
                }

                const refreshedPortfolio = await reconcileCommittedMutation({
                    operation,
                    barrierHandle: mutationBarrierHandle,
                    findPortfolio: (portfolio) => (
                        String(portfolio?.id) === String(saveAcknowledgement.portfolio_id)
                        && (portfolio?.portfolioVersion ?? portfolio?.portfolio_version)
                            === saveAcknowledgement.committed_portfolio_version
                        && (portfolio?.receiptId ?? portfolio?.receipt_id)
                            === saveAcknowledgement.receipt_id
                    ),
                    missingMessage: 'Refreshed optimizer portfolio was absent from the refreshed list.',
                });

                feedback.error(null);
                return {
                    success: true,
                    status: 'SUCCESS',
                    message: `Portfolio "${targetPortfolio.value}" re-optimized.`,
                    portfolio: refreshedPortfolio,
                };
            } catch (err) {
                console.error('Error refreshing saved optimized portfolio:', err);
                const failure = classifyAndRecordMutationFailure({
                    phase: mutationPhase,
                    error: err,
                    userScope,
                    barrierHandle: mutationBarrierHandle,
                    operation,
                });
                if (!failure.current) {
                    return {
                        success: false,
                        status: 'SUPERSEDED',
                    };
                }
                if (optimizerErrorCode(err) === 'optimizer_position_sizing_context_changed') {
                    void workspace.onPosSizingContextChanged?.();
                }
                const detail = optimizerErrorMessage(err, 'Unknown error.');
                const failureMessage = failure.status === 'COMMITTED_REFRESH_REQUIRED'
                    ? `The re-optimized portfolio save was acknowledged, but its complete saved state could not be confirmed. Do not retry; reload the application and inspect the saved portfolio. ${detail}`
                    : failure.status === 'REJECTED'
                        ? buildRefreshFailureMessage({
                            portfolioName: targetPortfolio?.value,
                            baseMessage: detail,
                        })
                        : failure.status === 'OUTCOME_UNKNOWN'
                            ? `The re-optimized portfolio save outcome could not be confirmed. Do not retry. Reload the application and inspect the saved portfolio before re-optimizing again. ${detail}`
                            : buildRefreshFailureMessage({
                                portfolioName: targetPortfolio?.value,
                                baseMessage: detail,
                            });
                feedback.error(failureMessage);
                return {
                    success: false,
                    status: failure.status,
                    message: failureMessage,
                    error: err,
                };
            } finally {
                if (operations.isCurrent(operation)) {
                    feedback.finish();
                }
            }
        };

        return { saveOptimizationAndWhatIfPortfolioData, refreshSavedOptimizedPortfolio };
    }, [operations, catalogue, results, workspace, feedback, execution]);
}
